import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';

interface SideSheetContextValue {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** The React content to render inside the sheet */
  sheetContent: React.ReactNode | null;
  /** Open the sheet with the given content */
  openSheet: (content: React.ReactNode) => void;
  /** Close the sheet */
  closeSheet: () => void;
}

const SideSheetContext = createContext<SideSheetContextValue>({
  isOpen: false,
  sheetContent: null,
  openSheet: () => {},
  closeSheet: () => {},
});

export const useSideSheet = () => useContext(SideSheetContext);

export const SideSheetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sheetContent, setSheetContent] = useState<React.ReactNode | null>(null);
  const location = useLocation();

  // Close the sheet on route change
  useEffect(() => {
    setSheetContent(null);
  }, [location.pathname]);

  const openSheet = useCallback((content: React.ReactNode) => { setSheetContent(content); }, []);
  const closeSheet = useCallback(() => { setSheetContent(null); }, []);

  const isOpen = sheetContent != null;

  return (
    <SideSheetContext.Provider value={{ isOpen, sheetContent, openSheet, closeSheet }}>
      {children}
    </SideSheetContext.Provider>
  );
};
